/** @file useToolAnnounce: the tool the toolbar names for a moment (switched to, or its key pressed again). */
import { useEffect, useState } from 'react';
import { isHeld } from '@zilo/pose3d-shortcuts/isHeld';
import { liveChord } from '../../shell/liveChord';
import type { EditorStore, Tool } from '../../state/editorTypes';
import { toolRepressed } from './toolRepressed';

/** How long the announcement stays (ms). */
const SHOW_MS = 1200;
/** The default chords when no live table is installed (the dev pages): Q W E R T. */
const DEFAULTS: Record<Tool, string> = { hand: 'KeyQ', move: 'KeyW', rotate: 'KeyE', scale: 'KeyR', ik: 'KeyT' };

/** The live chord of `tool.<tool>` (Settings overrides applied), else the default one. */
const chordOf = (tool: Tool): string => liveChord([`tool.${tool}`]) ?? DEFAULTS[tool];

/**
 * The tool and a counter that changes on every announcement (so a repeated press restarts it), or null: set when the
 * store's tool changes and when the current tool's key is pressed again (`toolRepressed`); cleared after `SHOW_MS`.
 */
export function useToolAnnounce(store: EditorStore): { tool: Tool; n: number } | null {
  const [shown, setShown] = useState<{ tool: Tool; n: number } | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let n = 0;
    const unsubscribe = store.subscribe((next, prev) => {
      if (next.tool === prev.tool && !toolRepressed(next, prev, chordOf, isHeld)) return;
      n += 1;
      setShown({ tool: next.tool, n });
      clearTimeout(timer);
      timer = setTimeout(() => { setShown(null); }, SHOW_MS);
    });
    return () => { unsubscribe(); clearTimeout(timer); };
  }, [store]);
  return shown;
}
